import { useEffect, useMemo, useState, useRef } from 'react';
import { View, Pressable, Modal, ScrollView, Platform, useWindowDimensions } from 'react-native';
import { Portal } from '@gorhom/portal';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { SELECT_PICKER_PORTAL_HOST } from '@/components/ui/SelectField';
import { coerceDate } from '@/utils/dateKeys';
import { useIosOverlayPresent } from '@/utils/useIosOverlayPresent';
import { InModalOverlay, useIosInModalOverlay } from '@/components/ui/InModalOverlay';
import { ThemedText, Caption } from './Text';
import { Card } from './Card';

/*
  Current calendar surface needs clearer layout cues:
  - Fixed aspect ratios produce cramped cells on narrow screens and don’t align with
    the header controls, which makes the month grid look disjointed.
  - Navigation buttons are small with tight padding, reducing tap targets and visual
    separation when using the year picker overlay.
  - Responsive behavior should keep the weekdays aligned in rows, keep the day chips
    readable, and ensure disabled/current-day states stay consistent across viewport
    widths before we adjust the controls or data strip.
*/

type CalendarProps = {
  visible: boolean;
  inline?: boolean;
  onClose: () => void;
  onSelectDate: (date: Date) => void;
  selectedDate?: Date | null;
  minDate?: Date;
  maxDate?: Date;
  title?: string;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatDisplayDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatMonthYear = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();

// Get all days for a month including leading/trailing days from adjacent months
const getDaysInMonth = (year: number, month: number) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday, 6 = Saturday

  const days: Array<{ date: number; month: number; year: number; isCurrentMonth: boolean; isDisabled: boolean }> = [];

  // Add leading days from previous month
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();

  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    days.push({
      date: prevMonthLastDay - i,
      month: prevMonth,
      year: prevYear,
      isCurrentMonth: false,
      isDisabled: false, // Will be checked later
    });
  }

  // Add current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      date: i,
      month: month,
      year: year,
      isCurrentMonth: true,
      isDisabled: false, // Will be checked later
    });
  }

  // Add trailing days from next month to complete the grid (6 rows = 42 days)
  const remainingDays = 42 - days.length;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;

  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      date: i,
      month: nextMonth,
      year: nextYear,
      isCurrentMonth: false,
      isDisabled: false, // Will be checked later
    });
  }

  return days;
};

export function Calendar({
  visible,
  inline = false,
  onClose,
  onSelectDate,
  selectedDate,
  minDate,
  maxDate,
  title = 'Select Date',
}: CalendarProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const useInModalOverlay = useIosInModalOverlay();
  const overlayVisible = useIosOverlayPresent(!inline && visible);
  /** iOS nested modals: percentage max-heights clip unpredictably — use pixel caps. */
  const sheetMaxHeight = useInModalOverlay
    ? Math.min(Math.round(windowHeight * 0.58), windowHeight - insets.top - 16)
    : Math.min(Math.round(windowHeight * 0.82), windowHeight - insets.top - 12);
  const dayCellMinHeight = useInModalOverlay ? 38 : 46;
  const sheetBottomPadding = useInModalOverlay
    ? Math.max(insets.bottom, theme.spacing(1))
    : 0;

  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const safeSelectedDate = useMemo(() => coerceDate(selectedDate ?? null), [selectedDate]);
  const safeMinDate = useMemo(() => coerceDate(minDate ?? null), [minDate]);
  const safeMaxDate = useMemo(() => coerceDate(maxDate ?? null), [maxDate]);

  const getInitialDate = () => {
    if (safeSelectedDate) return new Date(safeSelectedDate);
    if (safeMinDate) return new Date(safeMinDate);
    return new Date(today);
  };

  const [currentMonth, setCurrentMonth] = useState<Date>(getInitialDate);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const yearScrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    if (visible) {
      setCurrentMonth(getInitialDate());
      setShowYearPicker(false);
    }
  }, [visible, safeSelectedDate, safeMinDate, safeMaxDate]);

  const yearRange = useMemo(() => {
    const fallbackYear = today.getFullYear();
    const minYearCandidate = safeMinDate?.getFullYear() ?? fallbackYear - 5;
    const maxYearCandidate = safeMaxDate?.getFullYear() ?? fallbackYear + 15;
    const startYear = Math.min(minYearCandidate, fallbackYear - 5);
    const endYear = Math.max(maxYearCandidate, fallbackYear + 5);
    return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
  }, [safeMinDate, safeMaxDate, today]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const days = getDaysInMonth(year, month);

    // Mark disabled days
    return days.map((day) => {
      const dayDate = new Date(day.year, day.month, day.date);
      dayDate.setHours(0, 0, 0, 0);

      let isDisabled = false;
      if (safeMinDate) {
        const minDateNormalized = new Date(safeMinDate);
        minDateNormalized.setHours(0, 0, 0, 0);
        if (dayDate < minDateNormalized) {
          isDisabled = true;
        }
      }
      if (safeMaxDate) {
        const maxDateNormalized = new Date(safeMaxDate);
        maxDateNormalized.setHours(0, 0, 0, 0);
        if (dayDate > maxDateNormalized) {
          isDisabled = true;
        }
      }

      return { ...day, isDisabled };
    });
  }, [currentMonth, safeMinDate, safeMaxDate]);

  const calendarWeeks = useMemo(() => {
    const weeks: Array<Array<(typeof calendarDays)[number]>> = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }
    return weeks;
  }, [calendarDays]);

  const handleDayPress = (day: { date: number; month: number; year: number; isCurrentMonth: boolean; isDisabled: boolean }) => {
    if (day.isDisabled) return;

    const selected = new Date(day.year, day.month, day.date);
    selected.setHours(0, 0, 0, 0);
    onSelectDate(selected);
    onClose();
    setShowYearPicker(false);
  };

  const goToMonth = (offset: number) => {
    const next = new Date(currentMonth);
    next.setMonth(next.getMonth() + offset);
    setCurrentMonth(next);
  };

  const handleYearSelect = (year: number) => {
    const next = new Date(currentMonth);
    next.setFullYear(year);
    setCurrentMonth(next);
    setShowYearPicker(false);
  };

  // Scroll to selected year when year picker opens
  useEffect(() => {
    if (showYearPicker && yearScrollRef.current) {
      const selectedYear = currentMonth.getFullYear();
      const yearIndex = yearRange.findIndex((y) => y === selectedYear);
      if (yearIndex >= 0) {
        const itemWidth = 70 + theme.spacing(1);
        const scrollPosition = Math.max(0, yearIndex * itemWidth - 150);
        setTimeout(() => {
          yearScrollRef.current?.scrollTo({
            x: scrollPosition,
            animated: true,
          });
        }, 100);
      }
    }
  }, [showYearPicker, currentMonth, yearRange, theme]);

  const isPrevDisabled = useMemo(() => {
    if (!safeMinDate) return false;
    const minMonth = new Date(safeMinDate.getFullYear(), safeMinDate.getMonth(), 1);
    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return currentMonthStart <= minMonth;
  }, [currentMonth, safeMinDate]);

  const isNextDisabled = useMemo(() => {
    if (!safeMaxDate) return false;
    const maxMonth = new Date(safeMaxDate.getFullYear(), safeMaxDate.getMonth(), 1);
    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return currentMonthStart >= maxMonth;
  }, [currentMonth, safeMaxDate]);

  const isSelected = (day: { date: number; month: number; year: number }) => {
    if (!safeSelectedDate) return false;
    const dayDate = new Date(day.year, day.month, day.date);
    dayDate.setHours(0, 0, 0, 0);
    const selected = new Date(safeSelectedDate);
    selected.setHours(0, 0, 0, 0);
    return dayDate.getTime() === selected.getTime();
  };

  const isToday = (day: { date: number; month: number; year: number }) => {
    const dayDate = new Date(day.year, day.month, day.date);
    dayDate.setHours(0, 0, 0, 0);
    return dayDate.getTime() === today.getTime();
  };

  const navButtonSize = 44;

  if (inline && !visible) {
    return null;
  }

  const content = (
    <Card
      variant="elevated"
      elevation={2}
      padding={0}
      style={{
        borderTopLeftRadius: theme.radii.xl,
        borderTopRightRadius: theme.radii.xl,
        borderBottomLeftRadius: inline ? theme.radii.xl : 0,
        borderBottomRightRadius: inline ? theme.radii.xl : 0,
        width: '100%',
        maxHeight: inline ? undefined : sheetMaxHeight,
        paddingBottom: inline ? 0 : sheetBottomPadding,
        backgroundColor: theme.colors.surfaceElevated ?? theme.colors.surface,
        overflow: 'hidden',
      }}
    >
          {/* Drag Handle */}
          <View
            style={{
              alignSelf: 'center',
              width: 48,
              height: 4,
              borderRadius: theme.radii.pill,
              backgroundColor: theme.colors.borderMuted,
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(1.5),
            }}
          />

          {/* Header Section */}
          <View style={{ paddingHorizontal: theme.spacing(2), paddingBottom: theme.spacing(1) }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: theme.spacing(1),
              }}
            >
              <View style={{ flex: 1 }}>
                <ThemedText variant="bodyStrong" style={{ fontSize: 17, marginBottom: theme.spacing(0.25) }}>
                  {title}
                </ThemedText>
                {safeSelectedDate && (
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                    {formatDisplayDate(safeSelectedDate)}
                  </Caption>
                )}
              </View>
              <Pressable
                onPress={onClose}
                style={({ pressed }) => ({
                  padding: theme.spacing(1),
                  borderRadius: theme.radii.md,
                  backgroundColor: pressed ? theme.colors.borderMuted + '40' : 'transparent',
                })}
                accessibilityRole="button"
                accessibilityLabel="Close calendar"
              >
                <Ionicons name="close" size={20} color={theme.colors.text} />
              </Pressable>
            </View>

            {/* Month Navigation */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingVertical: theme.spacing(0.5),
              }}
            >
            <Pressable
                onPress={() => goToMonth(-1)}
                disabled={isPrevDisabled}
                style={({ pressed }) => ({
                  width: navButtonSize,
                  height: navButtonSize,
                  borderRadius: navButtonSize / 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor:
                    pressed && !isPrevDisabled ? theme.colors.borderMuted + '60' : 'transparent',
                  opacity: isPrevDisabled ? 0.35 : 1,
                })}
                accessibilityLabel="Previous month"
                accessibilityState={{ disabled: isPrevDisabled }}
              >
                <Ionicons
                  name="chevron-back"
                  size={20}
                  color={isPrevDisabled ? theme.colors.textSecondary : theme.colors.primary}
                />
              </Pressable>
              <Pressable
                onPress={() => setShowYearPicker((prev) => !prev)}
                style={({ pressed }) => ({
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(1),
                  borderRadius: theme.radii.lg,
                  borderWidth: 1,
                  borderColor: showYearPicker ? theme.colors.primary : theme.colors.borderMuted,
                  backgroundColor: pressed || showYearPicker ? theme.colors.borderMuted + '40' : 'transparent',
                  minHeight: 44,
                  minWidth: 160,
                  justifyContent: 'center',
                  flex: 1,
                  alignItems: 'center',
                })}
                accessibilityRole="button"
                accessibilityLabel="Toggle year picker"
                accessibilityState={{ expanded: showYearPicker }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                  <ThemedText variant="bodyStrong" style={{ letterSpacing: 0.5, fontSize: 14, textAlign: 'center' }}>
                    {formatMonthYear(currentMonth)}
                  </ThemedText>
                  <Ionicons
                    name={showYearPicker ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color={theme.colors.primary}
                  />
                </View>
              </Pressable>
              <Pressable
                onPress={() => goToMonth(1)}
                disabled={isNextDisabled}
                style={({ pressed }) => ({
                  width: navButtonSize,
                  height: navButtonSize,
                  borderRadius: navButtonSize / 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor:
                    pressed && !isNextDisabled ? theme.colors.borderMuted + '60' : 'transparent',
                  opacity: isNextDisabled ? 0.35 : 1,
                })}
                accessibilityLabel="Next month"
                accessibilityState={{ disabled: isNextDisabled }}
              >
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={isNextDisabled ? theme.colors.textSecondary : theme.colors.primary}
                />
              </Pressable>
            </View>
          </View>

          {/* Year Picker Section */}
          {showYearPicker && (
            <View
              style={{
                paddingHorizontal: theme.spacing(2),
                paddingBottom: theme.spacing(1),
                marginTop: theme.spacing(0.5),
              }}
            >
              <Pressable onPress={(e) => e.stopPropagation()}>
                <Card
                  variant="outlined"
                  padding={theme.spacing(1)}
                  style={{
                    backgroundColor: theme.colors.surfaceElevated ?? theme.colors.surface,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    borderRadius: theme.radii.lg,
                  }}
                >
                  <ScrollView
                    ref={yearScrollRef}
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    nestedScrollEnabled={true}
                    contentContainerStyle={{
                      flexDirection: 'row',
                      paddingHorizontal: theme.spacing(0.5),
                      alignItems: 'center',
                    }}
                    style={{ maxHeight: 96 }}
                  >
                    {yearRange.map((year) => {
                      const isSelected = year === currentMonth.getFullYear();
                      return (
                        <Pressable
                          key={year}
                          onPress={() => handleYearSelect(year)}
                          style={({ pressed }) => ({
                            paddingHorizontal: theme.spacing(2),
                            paddingVertical: theme.spacing(1),
                            borderRadius: theme.radii.md,
                            backgroundColor: isSelected
                              ? theme.colors.primary
                              : pressed
                                ? theme.colors.borderMuted + '40'
                                : theme.colors.surface,
                        borderWidth: 1,
                        borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                        marginRight: theme.spacing(0.75),
                            minWidth: 70,
                            alignItems: 'center',
                            justifyContent: 'center',
                          })}
                          accessibilityRole="button"
                          accessibilityState={{ selected: isSelected }}
                          accessibilityLabel={`Select year ${year}`}
                        >
                          <ThemedText
                            variant="bodyStrong"
                            style={{
                              color: isSelected ? theme.colors.onPrimary : theme.colors.text,
                              fontSize: 15,
                            }}
                          >
                            {year}
                          </ThemedText>
                        </Pressable>
                      );
                    })}
                  </ScrollView>
                </Card>
              </Pressable>
            </View>
          )}

          {/* Calendar Grid Section */}
          <View style={{ paddingHorizontal: theme.spacing(2), paddingBottom: theme.spacing(1.5) }}>
            <Card variant="outlined" padding={theme.spacing(1)} style={{ overflow: 'hidden' }}>
              {/* Weekday Headers */}
              <View
                style={{
                  flexDirection: 'row',
                  marginBottom: theme.spacing(0.5),
                }}
              >
                {WEEKDAYS.map((day) => (
                  <View
                    key={day}
                    style={{
                      flex: 1,
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingVertical: theme.spacing(0.5),
                    }}
                  >
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '600' }}>
                      {day}
                    </Caption>
                  </View>
                ))}
              </View>

              {/* Calendar Days Grid */}
              <View style={{ flexDirection: 'column' }}>
                {calendarWeeks.map((week, weekIndex) => (
                  <View
                    key={`week-${weekIndex}`}
                    style={{
                      flexDirection: 'row',
                      marginBottom:
                        weekIndex === calendarWeeks.length - 1 ? 0 : theme.spacing(0.35),
                      paddingHorizontal: theme.spacing(0.25),
                    }}
                  >
                    {week.map((day, dayIndex) => {
                      const dayIsSelected = isSelected(day);
                      const dayIsToday = isToday(day);
                      const isAdjacentMonth = !day.isCurrentMonth;
                      const baseTextColor = day.isDisabled
                        ? theme.colors.textSecondary
                        : dayIsSelected
                          ? theme.colors.onPrimary
                          : isAdjacentMonth
                            ? theme.colors.textSecondary
                            : dayIsToday
                              ? theme.colors.primary
                              : theme.colors.text;
                      const backgroundColor = dayIsSelected
                        ? theme.colors.primary
                        : isAdjacentMonth
                          ? theme.colors.surface
                          : 'transparent';

                      const isDisabled = !!day.isDisabled;
                      return (
                        <View
                          key={`${day.year}-${day.month}-${day.date}-${dayIndex}`}
                          style={{
                            flex: 1,
                            paddingHorizontal: theme.spacing(0.25),
                          }}
                        >
                          <Pressable
                            onPress={() => handleDayPress(day)}
                            disabled={isDisabled}
                            style={({ pressed }) => ({
                              minHeight: dayCellMinHeight,
                              borderRadius: theme.radii.md,
                              borderWidth: 1,
                              borderColor: dayIsSelected
                                ? theme.colors.primary
                                : theme.colors.borderMuted,
                              backgroundColor:
                                pressed && !isDisabled && !dayIsSelected
                                  ? theme.colors.borderMuted + '40'
                                  : backgroundColor,
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: isDisabled ? 0.35 : 1,
                            })}
                            accessibilityRole="button"
                            accessibilityState={{ selected: !!dayIsSelected, disabled: isDisabled }}
                            accessibilityLabel={`${day.date} ${
                              day.isCurrentMonth ? formatMonthYear(currentMonth) : 'adjacent month'
                            }`}
                          >
                            <ThemedText
                              variant="body"
                              style={{
                                fontSize: 14,
                                fontWeight: dayIsSelected ? '600' : dayIsToday ? '600' : '400',
                                color: baseTextColor,
                              }}
                            >
                              {day.date}
                            </ThemedText>
                          </Pressable>
                        </View>
                      );
                    })}
                  </View>
                ))}
              </View>
            </Card>
          </View>

          {/* Footer Hint */}
          {!selectedDate && !useInModalOverlay && (
            <View
              style={{
                alignItems: 'center',
                paddingBottom: theme.spacing(1.5),
                paddingHorizontal: theme.spacing(2),
              }}
            >
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                Tap a date to continue
              </Caption>
            </View>
          )}
    </Card>
  );

  if (inline && !visible) {
    return null;
  }

  if (inline) {
    return <View style={{ width: '100%' }}>{content}</View>;
  }

  if (!overlayVisible) {
    return null;
  }

  const sheetBody = (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.overlay,
        justifyContent: 'flex-end',
      }}
    >
      <Pressable
        style={{ flex: 1 }}
        onPress={() => {
          if (!showYearPicker) {
            onClose();
          }
        }}
        accessibilityRole="button"
      />
      {content}
    </View>
  );

  if (useInModalOverlay) {
    return (
      <InModalOverlay
        visible={overlayVisible}
        onClose={onClose}
        variant="bottom"
        onBackdropPress={() => {
          if (!showYearPicker) {
            onClose();
          }
        }}
      >
        {content}
      </InModalOverlay>
    );
  }

  const calendarModal = (
    <Modal
      visible={overlayVisible}
      animationType="slide"
      transparent
      statusBarTranslucent
      presentationStyle="overFullScreen"
      onRequestClose={onClose}
    >
      {sheetBody}
    </Modal>
  );

  return Platform.OS === 'ios' ? (
    <Portal hostName={SELECT_PICKER_PORTAL_HOST}>{calendarModal}</Portal>
  ) : (
    calendarModal
  );
}
